import { Command } from "commander";
import chalk from "chalk";
import ora from "ora";
import path from "path";
import fs from "fs";
import os from "os";
import { spawn } from "child_process";
import { checkJava } from "../checks/java.js";
import { checkAndroid } from "../checks/android.js";
import { findPackageJson, directoryExists, safeReadJson } from "../utils/files.js";
import { runCommand, runCommandStreaming } from "../utils/exec.js";
import { promptSelect, promptQuestion } from "../utils/prompt.js";
import { resolvePackageManager } from "../packageManager/packageManagerService.js";
import { detectProjectType } from "../project/projectDetector.js";
import { checkAndResolvePortConflict } from "../utils/portConflictResolver.js";
import { setupAdbReverse } from "../utils/adbReverse.js";

export const buildCommand = new Command("build")
  .description("Build and run the React Native application")
  .argument("[platform]", "Target platform ('android' or 'ios')", "android")
  .option("--mode <mode>", "Build mode ('debug' or 'release')")
  .option("--type <type>", "Build artifact type ('apk' or 'aab')")
  .option("--device <device>", "Specific device serial number to deploy to")
  .option("--install", "Install and run the build on a connected device")
  .option("--no-install", "Do not install the build on a connected device")
  .action(async (platformArg: string, options: { mode?: string; type?: string; device?: string; install?: boolean }) => {
    const cwd = process.cwd();
    const platform = platformArg.toLowerCase();

    if (platform !== "android") {
      console.log(chalk.red(`✖ Platform '${platform}' is not supported yet or invalid. For now, only 'android' is supported.`));
      process.exit(1);
    }

    console.log(chalk.blue.bold("🚀 Starting Android Build Orchestrator"));

    // 1. Verify environment
    const envSpinner = ora("Verifying JDK and Android SDK environment...").start();
    const javaChecks = await checkJava();
    const androidChecks = await checkAndroid();

    const hasJavaError = javaChecks.some(c => c.status === "error");
    const hasAndroidError = androidChecks.some(c => c.status === "error");

    if (hasJavaError || hasAndroidError) {
      envSpinner.fail("Environment check failed!");
      console.log(chalk.bold.red("\nPlease resolve the following errors before building:"));
      [...javaChecks, ...androidChecks].forEach(c => {
        if (c.status === "error") {
          console.log(chalk.red(`  ✖ ${c.name}: ${c.message}`));
          if (c.suggestion) console.log(chalk.yellow(`    Suggestion: ${c.suggestion}`));
        }
      });
      process.exit(1);
    }
    envSpinner.succeed("Environment verified successfully!");

    // Locate package.json and android directory
    const packageJsonPath = findPackageJson(cwd);
    if (!packageJsonPath) {
      console.log(chalk.red("✖ Could not find package.json in the current directory or parent directories."));
      process.exit(1);
    }
    const projectRoot = path.dirname(packageJsonPath);
    const androidDir = path.join(projectRoot, "android");

    if (!directoryExists(androidDir)) {
      console.log(chalk.red(`✖ Android native directory not found at: ${androidDir}`));
      console.log(chalk.yellow("Ensure you run 'kyro prebuild android' first."));
      process.exit(1);
    }

    // 2. Resolve build options
    let mode = options.mode;
    if (!mode) {
      if (process.stdout.isTTY) {
        mode = await promptSelect("Select build mode:", ["debug", "release"]);
      } else {
        mode = "debug";
      }
    }

    let buildType = options.type;
    if (!buildType) {
      if (process.stdout.isTTY) {
        buildType = await promptSelect("Select build type:", ["apk", "aab"]);
      } else {
        buildType = "apk";
      }
    }

    console.log();
    console.log(chalk.cyan(`Target Platform: android`));
    console.log(chalk.cyan(`Build Mode:      ${mode}`));
    console.log(chalk.cyan(`Artifact Type:   ${buildType}`));
    console.log();

    // Identify project type
    const projectType = detectProjectType(projectRoot);
    const isExpo = projectType.startsWith("expo");

    // Verify expo-dev-client package for Expo projects
    if (isExpo) {
      const pkg = safeReadJson(packageJsonPath);
      const dependencies = pkg ? (pkg.dependencies as Record<string, unknown> || {}) : {};
      const devDependencies = pkg ? (pkg.devDependencies as Record<string, unknown> || {}) : {};
      const hasDevClient = "expo-dev-client" in dependencies || "expo-dev-client" in devDependencies;

      if (!hasDevClient) {
        console.log(chalk.blue("\nℹ 'expo-dev-client' is missing from project dependencies. Installing automatically..."));
        const pmResult = await resolvePackageManager(projectRoot);
        const pm = pmResult.selected;
        let installCmd = "";
        if (pm === "yarn") {
          installCmd = "yarn add expo-dev-client";
        } else if (pm === "pnpm") {
          installCmd = "pnpm add expo-dev-client";
        } else if (pm === "bun") {
          installCmd = "bun add expo-dev-client";
        } else {
          installCmd = "npm install expo-dev-client";
        }

        const installSpinner = ora(`Installing expo-dev-client using ${pm}...`).start();
        const installResult = await runCommand(installCmd);
        if (installResult.error) {
          installSpinner.fail("Failed to automatically install expo-dev-client. Please install it manually.");
          console.error(chalk.red(installResult.stderr || installResult.stdout));
        } else {
          installSpinner.succeed("Successfully installed expo-dev-client!");
        }
      }
    }

    if (mode === "debug") {
      await checkAndResolvePortConflict(8081);
    }

    // 3. Handle Keystore setup for Release builds
    if (mode === "release") {
      const gradlePropertiesPath = path.join(androidDir, "gradle.properties");
      let gradleProps = "";
      if (fs.existsSync(gradlePropertiesPath)) {
        gradleProps = fs.readFileSync(gradlePropertiesPath, "utf-8");
      }

      let hasKeystoreConfig = false;
      const filePropMatch = gradleProps.match(/(?:MYAPP_RELEASE_STORE_FILE|RELEASE_STORE_FILE)\s*=\s*(.+)/);
      if (filePropMatch && filePropMatch[1]) {
        const configuredStoreFile = filePropMatch[1].trim();
        const absoluteKeystorePath = path.join(androidDir, "app", configuredStoreFile);
        if (fs.existsSync(absoluteKeystorePath)) {
          hasKeystoreConfig = true;
        }
      }

      if (!hasKeystoreConfig) {
        console.log(chalk.yellow("⚠️  No release signing configuration or keystore found in gradle.properties."));
        const shouldCreate = await promptQuestion("Would you like to generate a new release keystore now? (y/n): ");
        
        if (shouldCreate.toLowerCase() === "y" || shouldCreate.toLowerCase() === "yes") {
          console.log(chalk.blue("\n--- Keystore Generation Wizard ---"));
          const keystorePassword = await promptQuestion("Enter Keystore Password (min 6 characters): ");
          const alias = (await promptQuestion("Enter Key Alias (default: my-key-alias): ")) || "my-key-alias";
          
          if (keystorePassword.length < 6) {
            console.log(chalk.red("✖ Password must be at least 6 characters."));
            process.exit(1);
          }

          const defaultKeystoreName = "release.keystore";
          const keystoreInput = await promptQuestion(`Enter Keystore File Name (default: ${defaultKeystoreName}): `);
          let keystoreFileName = keystoreInput.trim() || defaultKeystoreName;
          if (!keystoreFileName.endsWith(".keystore") && !keystoreFileName.endsWith(".jks")) {
            keystoreFileName += ".keystore";
          }
          const keystorePath = path.join(androidDir, "app", keystoreFileName);

          const keytoolSpinner = ora("Generating release keystore file...").start();
          const keytoolCmd = `keytool -genkeypair -v -storetype PKCS12 -keystore "${keystorePath}" -alias "${alias}" -keyalg RSA -keysize 2048 -validity 10000 -storepass "${keystorePassword}" -keypass "${keystorePassword}" -dname "CN=Kyro, OU=KyroCLI, O=VibeCode, L=City, S=State, C=US"`;
          
          const keytoolResult = await runCommand(keytoolCmd);
          if (keytoolResult.error) {
            keytoolSpinner.fail("Keystore generation failed.");
            console.error(chalk.red(keytoolResult.stderr || keytoolResult.stdout));
            process.exit(1);
          }
          keytoolSpinner.succeed(`Keystore file created at: ${keystorePath}`);

          // Append to gradle.properties
          const configToAppend = `
# Release signing configuration generated by Kyro CLI
MYAPP_RELEASE_STORE_FILE=${keystoreFileName}
MYAPP_RELEASE_KEY_ALIAS=${alias}
MYAPP_RELEASE_STORE_PASSWORD=${keystorePassword}
MYAPP_RELEASE_KEY_PASSWORD=${keystorePassword}
`;
          fs.appendFileSync(gradlePropertiesPath, configToAppend, "utf-8");
          console.log(chalk.green("✓ Appended signing configurations to android/gradle.properties"));

          // Detect Kotlin DSL vs Groovy build.gradle
          const isKotlinDsl = fs.existsSync(path.join(androidDir, "app", "build.gradle.kts"));
          const buildFileName = isKotlinDsl ? "build.gradle.kts" : "build.gradle";

          // Print instructions for build.gradle
          console.log(chalk.bold.yellow("\n💡 IMPORTANT INSTRUCTION:"));
          console.log("Please make sure your " + chalk.cyan(`android/app/${buildFileName}`) + " is configured to use these signing properties.");
          console.log("Here is the configuration you should add to the signingConfigs and buildTypes blocks:\n");
          
          if (isKotlinDsl) {
            console.log(chalk.gray(`
android {
    ...
    signingConfigs {
        create("release") {
            if (project.hasProperty("MYAPP_RELEASE_STORE_FILE")) {
                // Configured with key: ${alias}
                storeFile = file(project.property("MYAPP_RELEASE_STORE_FILE") as String)
                storePassword = project.property("MYAPP_RELEASE_STORE_PASSWORD") as String // Uses password you entered
                keyAlias = project.property("MYAPP_RELEASE_KEY_ALIAS") as String
                keyPassword = project.property("MYAPP_RELEASE_KEY_PASSWORD") as String
            }
        }
    }
    buildTypes {
        getByName("release") {
            ...
            signingConfig = signingConfigs.getByName("release")
        }
    }
}
`));
          } else {
            console.log(chalk.gray(`
android {
    ...
    signingConfigs {
        release {
            if (project.hasProperty('MYAPP_RELEASE_STORE_FILE')) {
                // Configured with key: ${alias}
                storeFile file(MYAPP_RELEASE_STORE_FILE)
                storePassword MYAPP_RELEASE_STORE_PASSWORD // Uses password you entered
                keyAlias MYAPP_RELEASE_KEY_ALIAS
                keyPassword MYAPP_RELEASE_KEY_PASSWORD
            }
        }
    }
    buildTypes {
        release {
            ...
            signingConfig signingConfigs.release
        }
    }
}
`));
          }
          await promptQuestion("Press Enter to continue with building the app...");
        } else {
          console.log(chalk.yellow("Proceeding with build without signing configuration (build might fail or be unsigned)."));
        }
      }
    }

    // 4. Run Gradle Build
    const gradlewName = os.platform() === "win32" ? "gradlew.bat" : "./gradlew";
    const gradlewPath = path.join(androidDir, os.platform() === "win32" ? "gradlew.bat" : "gradlew");

    if (!fs.existsSync(gradlewPath)) {
      console.log(chalk.red("✖ Gradle wrapper script not found. Cleaning or repairing Gradle files..."));
      process.exit(1);
    }

    if (os.platform() !== "win32") {
      await runCommand(`chmod +x "${gradlewPath}"`);
    }

    // Determine target task
    let gradleTask = "";
    if (mode === "debug") {
      gradleTask = buildType === "apk" ? "assembleDebug" : "bundleDebug";
    } else {
      gradleTask = buildType === "apk" ? "assembleRelease" : "bundleRelease";
    }

    console.log(chalk.blue(`\n🔨 Running gradle task: ${gradleTask}...`));
    const buildResult = await runCommandStreaming(`cd "${androidDir}" && ${gradlewName} ${gradleTask}`, {
      prefix: "gradle",
      verbose: true,
    });

    if (buildResult.code !== 0) {
      console.log(chalk.red(`\n✖ Build failed with exit code ${buildResult.code}`));
      process.exit(1);
    }

    console.log(chalk.green("\n✓ Build completed successfully!"));

    // Find the output artifact path
    let artifactDir = path.join(androidDir, "app", "build", "outputs", buildType === "apk" ? "apk" : "bundle", mode);
    let extension = buildType === "apk" ? ".apk" : ".aab";
    let artifactPath = "";

    if (fs.existsSync(artifactDir)) {
      const files = fs.readdirSync(artifactDir);
      const matchedFile = files.find(f => f.endsWith(extension));
      if (matchedFile) {
        artifactPath = path.join(artifactDir, matchedFile);
      }
    }

    if (!artifactPath) {
      // Fallback search
      const fallbackSearchDir = path.join(androidDir, "app", "build", "outputs");
      const found = findFileInDir(fallbackSearchDir, `app-${mode}${extension}`, 5);
      if (found) {
        artifactPath = found;
      }
    }

    if (!artifactPath) {
      console.log(chalk.yellow(`⚠️ Could not locate the built ${buildType.toUpperCase()} file automatically.`));
      process.exit(0);
    }

    console.log(chalk.cyan(`Output path: ${artifactPath}`));

    // Copy artifact to project root under android-build
    const destDir = path.join(projectRoot, "android-build");
    if (!fs.existsSync(destDir)) {
      fs.mkdirSync(destDir, { recursive: true });
    }
    // Resolve app name, version name, and version code for output file naming
    let appDisplayName = "app";
    const appJsonPath = path.join(projectRoot, "app.json");
    const pkg = safeReadJson(packageJsonPath);
    
    if (fs.existsSync(appJsonPath)) {
      const appJson = safeReadJson(appJsonPath);
      if (appJson) {
        if (appJson.expo && typeof appJson.expo.name === "string") {
          appDisplayName = appJson.expo.name;
        } else if (typeof appJson.name === "string") {
          appDisplayName = appJson.name;
        } else if (typeof appJson.displayName === "string") {
          appDisplayName = appJson.displayName;
        }
      }
    }
    
    if (appDisplayName === "app" && pkg) {
      if (typeof pkg.displayName === "string" && pkg.displayName.length > 0) {
        appDisplayName = pkg.displayName;
      } else if (typeof pkg.name === "string" && pkg.name.length > 0) {
        appDisplayName = pkg.name;
      }
    }

    const appName = appDisplayName.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-");
    
    let versionName = "1.0.0";
    let versionCode = "1";
    
    const buildGradlePath = path.join(androidDir, "app", "build.gradle");
    if (fs.existsSync(buildGradlePath)) {
      const buildGradleContent = fs.readFileSync(buildGradlePath, "utf-8");
      
      const codeMatch = buildGradleContent.match(/versionCode\s+(\d+)/);
      if (codeMatch && codeMatch[1]) {
        versionCode = codeMatch[1];
      }
      
      const nameMatch = buildGradleContent.match(/versionName\s+"([^"]+)"/);
      if (nameMatch && nameMatch[1]) {
        versionName = nameMatch[1];
      } else if (pkg && pkg.version) {
        versionName = String(pkg.version);
      }
    } else if (pkg && pkg.version) {
      versionName = String(pkg.version);
    }

    const destFileName = `${appName}-${mode}-${versionName}-(${versionCode})${extension}`;
    const destPath = path.join(destDir, destFileName);
    try {
      fs.copyFileSync(artifactPath, destPath);
      console.log(chalk.green(`✓ Copied build artifact to: ${destPath}`));
    } catch (e: any) {
      console.log(chalk.yellow(`⚠️ Could not copy build artifact to root directory: ${e.message}`));
    }

    // Add to gitignore
    const gitignorePath = path.join(projectRoot, ".gitignore");
    let gitignoreContent = "";
    if (fs.existsSync(gitignorePath)) {
      gitignoreContent = fs.readFileSync(gitignorePath, "utf-8");
    }
    if (!gitignoreContent.includes("android-build/")) {
      const entry = gitignoreContent.endsWith("\n") ? "android-build/\n" : "\nandroid-build/\n";
      try {
        fs.appendFileSync(gitignorePath, entry, "utf-8");
        console.log(chalk.green("✓ Added 'android-build/' to project's .gitignore"));
      } catch (e: any) {
        console.log(chalk.yellow(`⚠️ Could not update .gitignore: ${e.message}`));
      }
    }

    // 5. Install and Run (only for APKs)
    let shouldInstall = options.install;
    if (shouldInstall === undefined) {
      if (process.stdout.isTTY) {
        const answer = await promptQuestion("Would you like to install and run the built APK onto a connected device? (y/n): ");
        shouldInstall = answer.toLowerCase() === "y" || answer.toLowerCase() === "yes";
      } else {
        shouldInstall = false;
      }
    }

    if (buildType === "apk" && shouldInstall) {
      // Check for connected devices
      const adbDevices = await runCommand("adb devices");
      const lines = adbDevices.stdout.split("\n").map(l => l.trim()).filter(l => l.length > 0);
      const devices = lines.slice(1).filter(l => l.endsWith("device")).map(l => l.split("\t")[0]);

      if (devices.length === 0) {
        console.log(chalk.yellow("\n⚠️ No active Android devices or emulators found. Skipping installation."));
        console.log(chalk.cyan(`You can manually install the APK from: ${artifactPath}`));
      } else {
        const targetDevice = options.device || devices[0];
        
        // Retrieve Package Name first
        let packageName = "";
        const buildGradlePath = path.join(androidDir, "app", "build.gradle");
        if (fs.existsSync(buildGradlePath)) {
          const buildGradleContent = fs.readFileSync(buildGradlePath, "utf-8");
          const match = buildGradleContent.match(/applicationId\s+"([^"]+)"/);
          if (match && match[1]) {
            packageName = match[1];
          }
        }
        if (!packageName) {
          const pkg = safeReadJson(packageJsonPath);
          if (pkg && pkg.name) {
            packageName = `com.${String(pkg.name).toLowerCase().replace(/[^a-z0-9]/g, "")}`;
          }
        }

        console.log(chalk.blue(`\n📲 Installing APK to device: ${targetDevice}...`));
        const installSpinner = ora("Installing...").start();
        const installResult = await runCommand(`adb -s ${targetDevice} install -r "${artifactPath}"`);
        let installFailed = !!installResult.error;
        let installOutput = (installResult.stderr + "\n" + installResult.stdout).trim();

        if (installFailed && installOutput.includes("INSTALL_FAILED_UPDATE_INCOMPATIBLE")) {
          installSpinner.fail("Installation failed due to signature mismatch (previously installed build has conflicting signature).");
          if (process.stdout.isTTY) {
            const answer = await promptQuestion("Would you like to uninstall the conflicting package from the device and try again? (y/n): ");
            if (answer.toLowerCase() === "y" || answer.toLowerCase() === "yes") {
              const uninstallSpinner = ora(`Uninstalling package ${packageName || 'app'}...`).start();
              let uninstallRes = await runCommand(`adb -s ${targetDevice} uninstall "${packageName}"`);
              if (uninstallRes.error) {
                // Try fallback to handle multiple user profiles / guest profiles / secure folders
                uninstallRes = await runCommand(`adb -s ${targetDevice} shell pm uninstall --user all "${packageName}"`);
              }

              if (uninstallRes.error) {
                uninstallSpinner.fail("Failed to uninstall previous build.");
                console.error(chalk.red(uninstallRes.stderr || uninstallRes.stdout));
                console.log(chalk.yellow("\n💡 Troubleshooting Tips:"));
                console.log("1. Go to Settings -> Apps -> " + (packageName || "your app") + " on your device and manually uninstall it for all users.");
                console.log("2. Check if the app is active in Settings -> Security -> Device Administrators and disable it.");
                console.log("3. If using Samsung, check if the app is installed inside Secure Folder / Private Share and remove it.");
              } else {
                uninstallSpinner.succeed("Conflicting version uninstalled successfully.");
                const reinstallSpinner = ora("Re-installing built APK...").start();
                const reinstallResult = await runCommand(`adb -s ${targetDevice} install -r "${artifactPath}"`);
                if (reinstallResult.error) {
                  reinstallSpinner.fail("Re-installation failed.");
                  console.error(chalk.red(reinstallResult.stderr || reinstallResult.stdout));
                } else {
                  reinstallSpinner.succeed("Installation successful!");
                  installFailed = false;
                }
              }
            }
          }
        } else if (installFailed) {
          installSpinner.fail("Installation failed.");
          console.error(chalk.red(installOutput));
        } else {
          installSpinner.succeed("Installation successful!");
        }

        if (!installFailed) {
          if (packageName) {
            console.log(chalk.blue(`\n🚀 Opening application: ${packageName}...`));
            const launchResult = await runCommand(`adb -s ${targetDevice} shell monkey -p ${packageName} -c android.intent.category.LAUNCHER 1`);
            if (launchResult.error) {
              console.log(chalk.yellow("⚠️ Failed to auto-open application automatically."));
            } else {
              console.log(chalk.green("✓ Application opened successfully!"));
            }
          } else {
            console.log(chalk.yellow("⚠️ Could not determine applicationId. Please launch the app manually."));
          }

          // 6. Connect Debug server if mode is debug
          if (mode === "debug") {
            console.log(chalk.blue("\n⚙️ Setting up reverse port mapping (8081)..."));
            await setupAdbReverse(targetDevice, 8081);

            console.log(chalk.blue("\n🌐 Starting Metro bundler server..."));
            
            const pm = "npm"; // fallback
            const isExpo = detectProjectType(projectRoot).startsWith("expo");
            const cmd = isExpo ? "npx expo start --android" : `${pm} run start || npx react-native start`;
            console.log(chalk.cyan(`Running: ${cmd}`));
            
            const metroProcess = spawn(os.platform() === "win32" ? "cmd.exe" : "sh", [
              os.platform() === "win32" ? "/c" : "-c",
              cmd
            ], {
              cwd: projectRoot,
              stdio: "inherit"
            });

            await new Promise<void>((resolve) => {
              metroProcess.on("close", () => resolve());
              metroProcess.on("exit", () => resolve());
            });
          }
        }
      }
    } else {
      console.log(chalk.yellow("\nℹ️ AAB builds are designed for App Store publishing and cannot be installed directly via adb."));
      console.log(chalk.cyan(`Path to bundle: ${artifactPath}`));
    }
  });

// Simple helper to search a file in directory
function findFileInDir(dir: string, fileName: string, maxDepth = 5, currentDepth = 0): string | null {
  if (currentDepth > maxDepth || !fs.existsSync(dir)) return null;
  try {
    const files = fs.readdirSync(dir);
    for (const file of files) {
      const fullPath = path.join(dir, file);
      const stat = fs.statSync(fullPath);
      if (stat.isDirectory()) {
        const found = findFileInDir(fullPath, fileName, maxDepth, currentDepth + 1);
        if (found) return found;
      } else if (file.toLowerCase() === fileName.toLowerCase() || file.endsWith(fileName)) {
        return fullPath;
      }
    }
  } catch {
    // ignore
  }
  return null;
}
